import { Check, Loader2, Pencil, Plus, Trash2, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import {
  useDeleteIngredientPackaging,
  usePostCreateIngredientPackaging,
  usePostUpdateIngredientPackagingUnit,
  usePutUpdateIngredientPackaging
} from "../../../../api/ingredients";
import ConfirmModal from "../../../../components/confirmViewModal/ConfirmModal";
import { FormInput } from "../../../../components/ui/Input/FormInput/FormInput";
import { FormSelect } from "../../../../components/ui/Input/FormSelector/FormSelect";
import { FormUnitInput } from "../../../../components/ui/Input/FormUnitInput/FormUnitInput";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from "../../../../components/ui/common/accordion";
import { Button } from "../../../../components/ui/common/button";
import { Form } from "../../../../components/ui/common/form";
import { Input } from "../../../../components/ui/common/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../../../../components/ui/common/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../../../../components/ui/common/table";
import { PackagingMaterialType } from "../../../../config/enums/PackagingMaterialType";
import { errorView, successMessage } from "../../../../helpers/ErrorHandler";
import { valueGraterThanZero } from "../../../../utils/utils";

interface InlinePackagingEditValues {
  packageName: string;
  packageMaterial: number | string;
  materialWeight: number | string;
  materialCost: number | string;
}

type PackagingType = "single-unit" | "sales-unit" | "bulk-unit" | "transport-unit";

interface PackagingLevelProps {
  packagingData: any[];
  packagingType: PackagingType;
  packagingTypeData: any[];
  totalWeightOfItem?: number;
  unitSizes: number;
  palletLength?: number;
  palletWidth?: number;
  ingredientId: string;
  supplierId: string;
  refetchPackagingData: () => void;
  packagingId: string | null;
  dataView?: boolean;
}

const LEVEL_CONFIG: Record<
  PackagingType,
  { title: string; label: string; type: string; name: string }
> = {
  "single-unit": {
    title: "Base unit packaging",
    label: "Unit size (kg)",
    type: "Base unit",
    name: "unitSize"
  },
  "sales-unit": {
    title: "Pack or inner pack packaging",
    label: "Number of Base Units",
    type: "Pack",
    name: "numberOfBaseUnits"
  },
  "bulk-unit": {
    title: "Case packaging",
    label: "Number of Packs",
    type: "Case",
    name: "numberOfPacks"
  },
  "transport-unit": {
    title: "Pallet packaging",
    label: "Number of Cases",
    type: "Pallet",
    name: "numberOfCases"
  }
};

const blank = (value: number | undefined) => (value && value > 0 ? value : "");

/**
 * Per-level packaging editor (single/sales/bulk/transport unit) — Tailwind
 * rebuild of the legacy `Packaging.tsx`
 * (src/screens/ingredients/ingredientRegister/Packaging.tsx), same two save
 * actions (top-of-row unit-size/pallet-dims vs. the packaging-name/material/
 * weight/cost sub-item form), same manual validation and API calls, just
 * off Semantic UI (`Accordion`/`Grid`/`Table`/`DropDown`) and onto the
 * Tailwind equivalents + the common `ui/Input` field components. Where the
 * legacy `InputNumber`'s `transform` prop blanked a `0` value so the
 * placeholder could show through, the field's own value is set to `""`
 * instead (see `blank()`) — same visual effect, no transform hook needed.
 *
 * Unlike the legacy flow, editing an existing sub-item (packageName/
 * packageMaterial/materialWeight/materialCost) row no longer populates the
 * form above the table — clicking its pencil icon turns that row's own
 * cells into inputs (checkmark/X to save/cancel), so the add form and its
 * validation stay untouched by an in-progress edit. The top-of-row unit-
 * size/pallet-dims fields are a single always-editable record (not a list),
 * so they're unaffected and keep their existing inline-save behavior.
 */
const PackagingLevel = ({
  packagingData,
  packagingType,
  packagingTypeData,
  totalWeightOfItem = 0,
  unitSizes,
  palletLength,
  palletWidth,
  ingredientId,
  supplierId,
  refetchPackagingData,
  packagingId,
  dataView = false
}: PackagingLevelProps) => {
  const config = LEVEL_CONFIG[packagingType];
  const isTransportUnit = packagingType === "transport-unit";

  const [visibleDeleteModal, setVisibleDeleteModal] = useState(false);
  const [selectData, setSelectData] = useState<any>(null);
  const [accordionValue, setAccordionValue] = useState<string>("");
  const [saveTransportUnitDataValidation, setSaveTransportUnitDataValidation] = useState(false);
  const [inlineEditId, setInlineEditId] = useState<string | null>(null);
  const [inlineValues, setInlineValues] = useState<InlinePackagingEditValues>({
    packageName: "",
    packageMaterial: "",
    materialWeight: "",
    materialCost: ""
  });

  const { mutate: mutatePostIngredient, isLoading } = usePostCreateIngredientPackaging();
  const { mutate: mutatePutIngredient, isLoading: isLoadingUpdate } =
    usePutUpdateIngredientPackaging();
  const { mutate: mutatePostPackageUnit, isLoading: isLoadingUnitValues } =
    usePostUpdateIngredientPackagingUnit();
  const { mutate: mutateDeletePackageUnit, isLoading: isDeletePackaging } =
    useDeleteIngredientPackaging();

  useEffect(() => {
    const hasData = packagingData && packagingData.length > 0;
    setAccordionValue(dataView || hasData ? "content" : "");
  }, [dataView, packagingData]);

  const methods = useForm();
  const { handleSubmit, control, reset, watch, setValue, setError, clearErrors } = methods;

  useEffect(() => {
    setValue(config.name, blank(unitSizes));
    setValue("palletWidth", blank(palletWidth));
    setValue("palletLength", blank(palletLength));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitSizes, selectData]);

  const onStartInlineEdit = (data: any) => {
    setInlineEditId(data._id);
    setInlineValues({
      packageName: data?.packageName || "",
      packageMaterial: data?.packageMaterial || 1,
      materialWeight: data?.materialWeight ?? "",
      materialCost: data?.materialCost ?? ""
    });
  };

  const onCancelInlineEdit = () => {
    setInlineEditId(null);
    setInlineValues({ packageName: "", packageMaterial: "", materialWeight: "", materialCost: "" });
  };

  const onSaveInlineEdit = (data: any) => {
    if (!String(inlineValues.packageName).trim()) {
      errorView("Packaging name is required");
      return;
    }
    const defaultType = Number(inlineValues.packageMaterial) || 0;
    if (defaultType === PackagingMaterialType.DEFAULT) {
      errorView("Packaging material is required");
      return;
    }
    if (!valueGraterThanZero(inlineValues.materialWeight)) {
      errorView("Material weight must be grater than 0");
      return;
    }

    const packageData = {
      data: {
        packageName: inlineValues.packageName,
        packageMaterial: defaultType,
        materialWeight: Number(inlineValues.materialWeight),
        materialCost: Number(inlineValues.materialCost) || 0,
        contentWeight: 0,
        totalWeightWithPackaging: 0
      },
      packagingType,
      ingredientId,
      supplierId,
      packagingId: data?._id || packagingId
    };

    mutatePutIngredient(packageData, {
      onSuccess: () => {
        successMessage("Package updated successfully");
        refetchPackagingData();
        onCancelInlineEdit();
      }
    });
  };

  const deletePackageData = () => {
    mutateDeletePackageUnit(
      {
        subPackagingId: selectData?._id,
        packagingType,
        packagingId,
        ingredientId
      },
      {
        onSuccess() {
          successMessage("Packaging data removed successfully");
          refetchPackagingData();
        }
      }
    );
  };

  const commentResetValue = (statusMessage: string) => {
    reset();
    setValue("packageMaterial", 0);
    successMessage(statusMessage);
  };

  const addPackageData = (packageData: any) => {
    mutatePostIngredient(packageData, {
      onSuccess: () => {
        commentResetValue("Package created successfully");
        setSelectData(null);
        refetchPackagingData();
      }
    });
  };

  const handlePackageValidation = (defaultType: number) => {
    if (unitSizes === 0) {
      setError(config.name, { type: "required", message: `${config.title} is required` });
      errorView(`${config.title} is required`);
      return false;
    }
    if (defaultType === PackagingMaterialType.DEFAULT) {
      setError("packageMaterial", { type: "required", message: "Packaging material is required" });
      errorView("Packaging material is required");
      return false;
    }
    return true;
  };

  // Only reachable from the add form now — editing an existing sub-item
  // goes through `onSaveInlineEdit` instead (see the file doc comment).
  const packageDataInsert = (data: any) => {
    const defaultType = Number(data.packageMaterial) || 0;
    data.packageMaterial = defaultType;
    data.contentWeight = 0;
    data.totalWeightWithPackaging = 0;
    data.materialWeight = Number(data.materialWeight);
    data.materialCost = Number(data.materialCost);

    const packageData = {
      data,
      packagingType,
      ingredientId,
      supplierId,
      packagingId
    };

    if (handlePackageValidation(defaultType)) {
      addPackageData(packageData);
    }
  };

  const unitSizeChange = useWatch({ control, name: config.name, defaultValue: 0 });

  const checkValueISChange = useCallback(
    () => Number(unitSizeChange) !== 0 && Number(unitSizeChange) !== Number(unitSizes),
    [unitSizeChange, unitSizes]
  );

  const palletWidthData = watch("palletWidth");
  const palletLengthData = watch("palletLength");
  const palletUnitData = watch(config.name);

  const onChangeUnitData = () => {
    const updatePackingData: Record<string, unknown> = {
      data: { [config.name]: unitSizeChange },
      ingredientId,
      supplierId,
      packagingId
    };

    if (isTransportUnit) {
      (updatePackingData.data as Record<string, unknown>).palletWidth = palletWidthData;
      (updatePackingData.data as Record<string, unknown>).palletLength = palletLengthData;
    }

    mutatePostPackageUnit(updatePackingData, {
      onSuccess() {
        refetchPackagingData();
      }
    });
  };

  useEffect(() => {
    /* eslint-disable eqeqeq -- loose match preserved from Packaging.tsx (string vs number form values) */
    const isSame =
      unitSizes == palletUnitData &&
      palletLength == palletLengthData &&
      palletWidth == palletWidthData;
    /* eslint-enable eqeqeq */
    const isValid = palletUnitData > 0 && palletWidthData > 0 && palletLengthData > 0;
    setSaveTransportUnitDataValidation(!isSame && isValid);
  }, [palletUnitData, palletWidthData, palletLengthData, unitSizes, palletLength, palletWidth]);

  const isMutating = isLoading || isLoadingUpdate || isDeletePackaging || isLoadingUnitValues;

  const showTopSaveButton = isTransportUnit
    ? saveTransportUnitDataValidation
    : checkValueISChange();

  return (
    <Form {...methods}>
      <div className="tw-border-t tw-border-border tw-pt-3 first:tw-border-t-0 first:tw-pt-0">
        <Accordion
          type="single"
          collapsible
          value={accordionValue}
          onValueChange={setAccordionValue}
        >
          <AccordionItem value="content" className="tw-border-b-0">
            <AccordionTrigger className="tw-px-0 tw-pt-1 tw-pb-2 hover:tw-no-underline">
              <span className="tw-text-sm tw-font-semibold tw-text-foreground">{config.title}</span>
            </AccordionTrigger>
            <AccordionContent>
              {isMutating ? (
                <div className="tw-flex tw-items-center tw-justify-center tw-py-6">
                  <Loader2 className="tw-h-5 tw-w-5 tw-animate-spin tw-text-primary" />
                </div>
              ) : (
                <>
                  {!dataView ? (
                    <div className="tw-mb-3 tw-flex tw-flex-wrap tw-items-start tw-gap-3">
                      <FormUnitInput
                        control={control}
                        name={config.name}
                        label={config.label}
                        placeholder={unitSizes > 0 ? String(unitSizes) : "0"}
                        validate={{
                          valueCheck: (v: any) =>
                            valueGraterThanZero(v) || `${config.label} must be grater than 0`
                        }}
                      />

                      {isTransportUnit ? (
                        <>
                          <FormUnitInput
                            control={control}
                            name="palletWidth"
                            label="Pallet Width"
                            placeholder={packagingData?.length > 0 ? "0" : "width (cm)"}
                            unitType="cm"
                            validate={{
                              valueCheck: (v: any) =>
                                valueGraterThanZero(v) || "Pallet Width must be grater than 0"
                            }}
                          />
                          <FormUnitInput
                            control={control}
                            name="palletLength"
                            label="Pallet Length"
                            placeholder={packagingData?.length > 0 ? "0" : "length (cm)"}
                            unitType="cm"
                            validate={{
                              valueCheck: (v: any) =>
                                valueGraterThanZero(v) || "Pallet Length must be grater than 0"
                            }}
                          />
                        </>
                      ) : null}

                      {showTopSaveButton ? (
                        <div className="tw-flex tw-flex-col tw-gap-2">
                          <span
                            aria-hidden="true"
                            className="tw-invisible tw-text-sm tw-font-medium tw-leading-none"
                          >
                            &nbsp;
                          </span>
                          <Button type="button" size="sm" onClick={onChangeUnitData}>
                            {unitSizes > 0 ||
                            (isTransportUnit &&
                              (Number(palletLength) > 0 || Number(palletWidth) > 0))
                              ? "Update"
                              : "Save"}
                          </Button>
                        </div>
                      ) : null}

                      <div className="tw-ml-auto tw-text-right">
                        <p className="tw-text-xs tw-text-muted-foreground">
                          Total weight of {config.type}
                        </p>
                        <p className="tw-text-sm tw-font-medium tw-text-foreground">
                          {totalWeightOfItem}
                        </p>
                      </div>
                    </div>
                  ) : null}

                  {!dataView ? (
                    <div className="tw-mb-4 tw-grid tw-grid-cols-2 tw-items-start tw-gap-3 md:tw-grid-cols-[1fr_1fr_1fr_1fr_auto]">
                      <FormInput
                        control={control}
                        name="packageName"
                        label="Packaging Name"
                        placeholder="Packaging Name"
                        required
                        errorMessage="Packaging name is required"
                      />
                      <FormSelect
                        control={control}
                        name="packageMaterial"
                        label="Packaging Material"
                        options={packagingTypeData}
                        placeholder="Packaging Material"
                        onChange={() => clearErrors("packageMaterial")}
                      />
                      <FormUnitInput
                        control={control}
                        name="materialWeight"
                        label="Material Weight(kg)"
                        placeholder="0.00"
                        required
                        errorMessage="Material Weight is required"
                        validate={{
                          valueCheck: (v: any) =>
                            valueGraterThanZero(v) || "Material weight must be grater than 0"
                        }}
                      />
                      <FormUnitInput
                        control={control}
                        name="materialCost"
                        label="Material Cost per unit"
                        placeholder="0.00"
                      />
                      <div className="tw-flex tw-flex-col tw-gap-2">
                        <span
                          aria-hidden="true"
                          className="tw-invisible tw-text-sm tw-font-medium tw-leading-none"
                        >
                          &nbsp;
                        </span>
                        <Button
                          type="button"
                          size="icon"
                          className="tw-h-10 tw-w-10 tw-justify-self-start"
                          onClick={handleSubmit(data => packageDataInsert(data))}
                        >
                          <Plus className="tw-h-5 tw-w-5" />
                        </Button>
                      </div>
                    </div>
                  ) : null}

                  {packagingData?.length > 0 ? (
                    <div className="tw-overflow-x-auto tw-rounded-md tw-border tw-border-border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Packaging Name</TableHead>
                            <TableHead>Packaging Material</TableHead>
                            <TableHead>Material Weight</TableHead>
                            <TableHead>Material Cost</TableHead>
                            <TableHead className="tw-text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {packagingData.map((data: any, index: number) => {
                            const material = packagingTypeData?.find(
                              (e: any) => e.value === data?.packageMaterial
                            );
                            const isEditingRow = inlineEditId === data?._id;
                            return (
                              <TableRow key={data?._id || index}>
                                <TableCell>
                                  {isEditingRow ? (
                                    <Input
                                      className="tw-h-8"
                                      value={inlineValues.packageName}
                                      onChange={e =>
                                        setInlineValues(prev => ({
                                          ...prev,
                                          packageName: e.target.value
                                        }))
                                      }
                                    />
                                  ) : (
                                    data?.packageName ?? ""
                                  )}
                                </TableCell>
                                <TableCell>
                                  {isEditingRow ? (
                                    <Select
                                      value={String(inlineValues.packageMaterial)}
                                      onValueChange={value =>
                                        setInlineValues(prev => ({ ...prev, packageMaterial: value }))
                                      }
                                    >
                                      <SelectTrigger className="tw-h-8 tw-w-full">
                                        <SelectValue placeholder="Packaging Material" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {packagingTypeData?.map((option: any) => (
                                          <SelectItem key={option.value} value={String(option.value)}>
                                            {option.text}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  ) : (
                                    material?.text || ""
                                  )}
                                </TableCell>
                                <TableCell>
                                  {isEditingRow ? (
                                    <Input
                                      type="number"
                                      className="tw-h-8"
                                      value={inlineValues.materialWeight}
                                      onChange={e =>
                                        setInlineValues(prev => ({
                                          ...prev,
                                          materialWeight: e.target.value
                                        }))
                                      }
                                    />
                                  ) : (
                                    data?.materialWeight ?? ""
                                  )}
                                </TableCell>
                                <TableCell>
                                  {isEditingRow ? (
                                    <Input
                                      type="number"
                                      className="tw-h-8"
                                      value={inlineValues.materialCost}
                                      onChange={e =>
                                        setInlineValues(prev => ({
                                          ...prev,
                                          materialCost: e.target.value
                                        }))
                                      }
                                    />
                                  ) : (
                                    data?.materialCost ?? ""
                                  )}
                                </TableCell>
                                <TableCell className="tw-text-right">
                                  {!dataView ? (
                                    <div className="tw-flex tw-justify-end tw-gap-3">
                                      {isEditingRow ? (
                                        <>
                                          <button type="button" onClick={() => onSaveInlineEdit(data)}>
                                            <Check className="tw-h-4 tw-w-4 tw-text-primary" />
                                          </button>
                                          <button type="button" onClick={onCancelInlineEdit}>
                                            <X className="tw-h-4 tw-w-4 tw-text-destructive" />
                                          </button>
                                        </>
                                      ) : (
                                        <>
                                          <button
                                            type="button"
                                            onClick={() => onStartInlineEdit(data)}
                                          >
                                            <Pencil className="tw-h-4 tw-w-4 tw-text-muted-foreground" />
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              setSelectData(data);
                                              setVisibleDeleteModal(true);
                                            }}
                                          >
                                            <Trash2 className="tw-h-4 tw-w-4 tw-text-muted-foreground" />
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  ) : null}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <p className="tw-text-sm tw-text-muted-foreground">
                      {config.title} is not available
                    </p>
                  )}
                </>
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <ConfirmModal
          viewModal={visibleDeleteModal}
          closeModal={() => setVisibleDeleteModal(false)}
          cancel={() => {
            setVisibleDeleteModal(false);
            setSelectData(null);
          }}
          approve={() => {
            setVisibleDeleteModal(false);
            deletePackageData();
          }}
          title="Delete package data"
          subTitle="Are you sure you want to delete packaging data?"
        />
      </div>
    </Form>
  );
};

export default PackagingLevel;
